import { lazy, useEffect } from "react";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import AppRoute from "./route";
import ProtectedRoute from "./ProtectedRoute";
import Layout from "../container/layout";
import { isTokenValid } from "../utils/secureStorage";

const LoginPage = lazy(() => import("../pages/loginPage"));
const DashboardPage = lazy(() => import("../pages/dashboardPage"));
const UnionPage = lazy(() => import("../pages/unionPage"));
const AddUnionPage = lazy(() => import("../pages/unionPage/addUnionPage"));
const EditUnionPage = lazy(() => import("../pages/unionPage/editUnionPage"));
const PlantsPage = lazy(() => import("../pages/plantsPage"));
const AddPlantsPage = lazy(() => import("../pages/plantsPage/addPlantsPage"));
const ViewPlantsPage = lazy(() => import("../pages/plantsPage/viewPlantsPage"));
const EditPlantsPage = lazy(() => import("../pages/plantsPage/editPlantsPage"));
const BMCPage = lazy(() => import("../pages/bmcPage"));
const AddBMCPage = lazy(() => import("../pages/bmcPage/addBMCPage"));
const ViewBMCPage = lazy(() => import("../pages/bmcPage/viewBMCPage"));
const EditBMCPage = lazy(() => import("../pages/bmcPage/editBMCPage"));
const DCSPage = lazy(() => import("../pages/dcsPage"));
const AddDCSPage = lazy(() => import("../pages/dcsPage/addDCSPage"));
const ViewDCSPage = lazy(() => import("../pages/dcsPage/viewDCSPage"));
const EditDCSPage = lazy(() => import("../pages/dcsPage/editDCSPage"));
const FarmersPage = lazy(() => import("../pages/farmersPage"));
const AddFarmersPage = lazy(() =>
  import("../pages/farmersPage/addFarmersPage")
);
const EditFarmersPage = lazy(() =>
  import("../pages/farmersPage/editFarmersPage")
);
const ViewFarmersPage = lazy(() =>
  import("../pages/farmersPage/viewFarmersPage")
);

const VehiclesPage = lazy(() => import("../pages/vehiclesPage"));
const AddVehiclesPage = lazy(() =>
  import("../pages/vehiclesPage/addVehiclesPage")
);
const ViewVehiclesPage = lazy(() =>
  import("../pages/vehiclesPage/viewVehiclesPage")
);
const EditVehiclesPage = lazy(() =>
  import("../pages/vehiclesPage/editVehiclesPage")
);
const LiveTrackingPage = lazy(() =>
  import("../pages/vehiclesPage/liveTrackingPage")
);
const DriversPage = lazy(() => import("../pages/driversPage"));
const AddDriversPage = lazy(() =>
  import("../pages/driversPage/addDriversPage")
);
const ViewDriversPage = lazy(() =>
  import("../pages/driversPage/viewDriversPage")
);
const EditDriversPage = lazy(() =>
  import("../pages/driversPage/editDriversPage")
);
const ViewUnionPage = lazy(() => import("../pages/unionPage/viewUnionPage"));
const GpsPage = lazy(() => import("../pages/gpsPage"));
const AddGpsPage = lazy(() => import("../pages/gpsPage/addGPSPage"));
const ViewGpsPage = lazy(() => import("../pages/gpsPage/viewGPSPage"));
const EditGpsPage = lazy(() => import("../pages/gpsPage/editGPSPage"));
const CctvPage = lazy(() => import("../pages/cctvPage"));
const AddCctvPage = lazy(() => import("../pages/cctvPage/addCCTVPage"));
const SmartLockPage = lazy(() => import("../pages/smartLocksPage"));
const AddSmartLockPage = lazy(() =>
  import("../pages/smartLocksPage/addSmartLocksPage")
);
const MappingPage = lazy(() => import("../pages/mappingPage"));
const AddMappingPage = lazy(() =>
  import("../pages/mappingPage/addMappingPage")
);

const CameraPage = lazy(() => import("../pages/cameraPage"));

const RouteCreationPage = lazy(() => import("../pages/routeCreationPage"));
const CreateRoutePage = lazy(() =>
  import("../pages/routeCreationPage/createRoutePage")
);
const RateMatrixPage = lazy(() => import("../pages/rateMatrixPage"));
const SettingPage = lazy(() => import("../pages/settingPage"));
const UserRoleManagementPage = lazy(() =>
  import("../pages/userRoleManagementPage")
);
const AssignRolePage = lazy(() =>
  import("../pages/userRoleManagementPage/assignRoleContent")
);
const UserFlowForCreationPage = lazy(() =>
  import("../pages/userFlowForCreationPage")
);
const CreateUserPage = lazy(() =>
  import("../pages/userFlowForCreationPage/createUserPage")
);
const ViewUserPage = lazy(() =>
  import("../pages/userFlowForCreationPage/viewUserPage")
);

// live gps track
const LiveGpsTrackingPage = lazy(() => import("../pages/liveGpsTrackingPage"));
// trip navigator
const TripNavigatorPage = lazy(() => import("../pages/tripNavigatorPage"));
// merge tracking
const MergeTrackingPage = lazy(() =>
  import("../pages/tripNavigatorPage/mergeTrackingPage")
);
// tracking details
const MergeTrackingDetailedStatusPage = lazy(() =>
  import(
    "../pages/tripNavigatorPage/mergeTrackingPage/mergeTrackingDetailedStatusPage"
  )
);

// dcs secretary details
const DcsSecretaryDetailsPage = lazy(() =>
  import("../pages/dcsSecretaryDetailsPage")
);

// dcs daily report
const DcsDailyReportPage = lazy(() =>
  import("../pages/dcsPage/dcsDailyReportPage")
);
const AdminDcsDailyReportPage = lazy(() =>
  import("../pages/adminPage/adminDcsPage/adminDcsDailyReportPage")
);

// maker dashboard
const MakerDashboardPage = lazy(() =>
  import("../pages/makerPages/makerDashboardPage")
);
//milk procurement dashboard
const ProcurementDashboardPage = lazy(() =>
  import("../pages/milkProcurementPage/procurementDashboardPage")
);
//milk procurement farmer list
const ProcurementFarmerListPage = lazy(() =>
  import("../pages/milkProcurementPage/procurementFarmerListPage")
);
//milk procurement dcs list
const ProcurementDcsListPage = lazy(() =>
  import("../pages/milkProcurementPage/procurementDcsListPage")
);
//milk procurement deduction
const ProcurementDeductionPage = lazy(() =>
  import("../pages/milkProcurementPage/procurementDeductionPage")
);
//milk procurement deduction – DCS wise list (admin / union / approver)
const DcsDeductionPage = lazy(() =>
  import("../pages/milkProcurementPage/procurementDeductionPage/dcsDeductionPage")
);
//milk procurement payout
const PayoutDcsListPage = lazy(() =>
  import("../pages/milkProcurementPage/payoutPage/payoutDcsListPage")
);
const PayoutFarmerListPage = lazy(() =>
  import("../pages/milkProcurementPage/payoutPage/payoutFarmerListPage")
);
//payment cycle
const AdminPaymentCyclePage = lazy(() =>
  import("../pages/adminPage/adminPaymentCyclePage")
);
//final payment sheet
const FinalPaymentSheetPage = lazy(() =>
  import("../pages/milkProcurementPage/finalPaymentSheetPage")
);
const FinalPaymentFarmerListPage = lazy(() =>
  import("../pages/milkProcurementPage/finalPaymentSheetPage/finalPaymentFarmerListPage")
);
//upload payment details
const UploadPaymentSheetPage = lazy(() =>
  import("../pages/milkProcurementPage/paymentDetailsPage/uploadPaymentSheetPage")
);
//payment list
const PaymentListPage = lazy(() =>
  import("../pages/milkProcurementPage/paymentDetailsPage/paymentListPage")
);
//samudra milk reconcilation
const SamudraMilkReconcilationPage = lazy(() =>
  import("../pages/samudraMilkReconcilationPage")
);
// Checker
const CheckerDashboardPage = lazy(() =>
  import("../pages/checker/checkerDashboardPage")
);
const CheckerFarmerListPage = lazy(() =>
  import("../pages/checker/checkerFarmerListPage")
);
const CheckerFarmerDetailsPage = lazy(() =>
  import("../pages/checker/checkerFarmerDetailsPage")
);
const CheckerProfilePage = lazy(() =>
  import("../pages/checker/checkerProfilePage")
);
const CheckerSelfProfilingPage = lazy(() =>
  import("../pages/checker/checkerSelfProfilingPage")
);

// Approver
const ApproverDashboardPage = lazy(() =>
  import("../pages/approver/approverDashboardPage")
);
const ApproverFarmerListPage = lazy(() =>
  import("../pages/approver/approverFarmerListPage")
);
const ApproverFarmerDetailsPage = lazy(() =>
  import("../pages/approver/approverFarmerDetailsPage")
);
const ApproverProfilePage = lazy(() =>
  import("../pages/approver/approverProfilePage")
);
const ApproverSelfProfilingPage = lazy(() =>
  import("../pages/approver/approverSelfProfilingPage")
);

// Admin
const AdminDashboardPage = lazy(() =>
  import("../pages/adminPage/adminDashboardPage")
);
//DBT-PROCUREMENT DASHBOARD
const ProcurementAdminDashboardPage = lazy(() =>
  import("../pages/adminPage/newadminDashboardPage/procurementDashboardPage")
);
//Quality Monitoring Page
const QualityMonitoringPage = lazy(() =>
  import("../pages/adminPage/newadminDashboardPage/qualityMonitoringPage")
);
//Quality Monitoring Dat Page
const QualityMonitoringDatPage = lazy(() =>
  import("../pages/adminPage/newadminDashboardPage/qualityMonitoringDatPage")
);
//DCS Wise Procurement Report Page
const DcsWiseProcurementReportPage = lazy(() => import("../pages/adminPage/newadminDashboardPage/dcsWiseProcurementReportPage"));
//DCS Wise Procurement Report Dat Page
const DcsWiseProcurementReportDatPage = lazy(() => import("../pages/adminPage/newadminDashboardPage/dcsWiseProcurementReportDatPage"));
//Farmer Ledger Report Page
const FarmerLedgerReportPage = lazy(() => import("../pages/adminPage/newadminDashboardPage/farmerLedgerReportPage"));
//vasishta sales dashboard
const VasishtaDashboardPage = lazy(() => import("../pages/adminPage/vasishtaDashboardPage"));
//SAP purchase order dashboard
const SapDashboardPage = lazy(() => import("../pages/adminPage/sapDashboardPage"));
//SAP purchase order list (report)
const SapMaterialReportPage = lazy(() => import("../pages/adminPage/sapMaterialReportPage"));
//SAP material master
const SapMaterialMasterPage = lazy(() => import("../pages/adminPage/sapMaterialMasterPage"));
const MakerDetailsPage = lazy(() =>
  import("../pages/adminPage/adminDashboardPage/makerDetailsPage")
);
const CheckerDetailsPage = lazy(() =>
  import("../pages/adminPage/adminDashboardPage/checkerDetailsPage")
);
const ApproverDetailsPage = lazy(() =>
  import("../pages/adminPage/adminDashboardPage/approverDetailsPage")
);
const AdminDataDashboardPage = lazy(() =>
  import("../pages/adminPage/newadminDashboardPage/mainDashboardPage")
);

const cctvDashboardPage = lazy(() =>
  import("../pages/adminPage/newadminDashboardPage/cctvDashboardPage")
);
const AdminDCSPage = lazy(() => import("../pages/adminPage/adminDcsPage"));
const ViewDcsPage = lazy(() =>
  import("../pages/adminPage/adminDcsPage/viewDcsPage")
);
const AdminFarmerPage = lazy(() =>
  import("../pages/adminPage/adminFarmerPage")
);
const ViewFarmerPage = lazy(() =>
  import("../pages/adminPage/adminFarmerPage/viewFarmerPage")
);
const AdminMapDashboardPage = lazy(() => import("../pages/adminPage/adminMapDashboardPage"));
const AdminHeatMapPage = lazy(() => import("../pages/adminPage/adminHeatMapPage"));
const MilkAnalyticsPage = lazy(() => import("../pages/adminPage/adminAnalyticsPage/MilkAnalyticsPage"));
const ProductAnalyticsPage = lazy(() => import("../pages/adminPage/adminAnalyticsPage/ProductAnalyticsPage"));
const AdminRetailerPage = lazy(() => import("../pages/adminPage/adminRetailerPage"));
//admin report page
const DailyReportPage = lazy(() =>
  import("../pages/adminPage/adminReportPage/dailyReportPage")
);
const UnionWiseReportPage = lazy(() =>
  import("../pages/adminPage/adminReportPage/unionWiseReportPage")
);

// Union Admin
const UnionAdminDashboardPage = lazy(() =>
  import("../pages/unionadminPage/unionDashboardPage")
);
const UnionAdminFarmerListPage = lazy(() =>
  import("../pages/unionadminPage/unionFarmerListPage")
);
const UnionAdminDcsListPage = lazy(() =>
  import("../pages/unionadminPage/unionDcsListPage")
);
const UnionProcurementDashboardPage = lazy(() =>
  import("../pages/unionadminPage/unionProcurementDashboardPage")
);

// user role mange - admin side
const AdminCreateGroupPage = lazy(() =>
  import("../pages/adminPage/adminUserRoleManagePage/createGroupPage")
);
const AdminCreateUserPage = lazy(() =>
  import("../pages/adminPage/adminUserRoleManagePage/createUserPage")
);
const AdminEditGroupPage = lazy(() =>
  import("../pages/adminPage/adminUserRoleManagePage/editGroupPage")
);
const UserGroupTable = lazy(() =>
  import("../pages/adminPage/adminUserRoleManagePage/userGroupTablePage")
);


const FarmerOnboardingPage = lazy(() =>
  import("../pages/farmerOnboardingPage")
);


const publicRoutes = [{ path: "/", exact: true, component: LoginPage },
{ path: "/farmer", exact: true, component: FarmerOnboardingPage }
];


const privateRoutes = [
  { path: "/dashboard", exact: true, component: DashboardPage },
  //data - management
  { path: "/master/unionmaster", exact: true, component: UnionPage },
  {
    path: "/master/unionmaster/add-union",
    exact: true,
    component: AddUnionPage,
  },
  {
    path: "/master/unionmaster/edit-union/:unionId",
    exact: true,
    component: EditUnionPage,
  },
  {
    path: "/master/unionmaster/view-union/:unionId",
    exact: true,
    component: ViewUnionPage,
  },
  { path: "/master/plantmaster", exact: true, component: PlantsPage },
  {
    path: "/master/plantmaster/add-plants",
    exact: true,
    component: AddPlantsPage,
  },
  {
    path: "/master/plantmaster/view-plant/:plantId",
    exact: true,
    component: ViewPlantsPage,
  },
  {
    path: "/master/plantmaster/edit-plant/:plantId",
    exact: true,
    component: EditPlantsPage,
  },
  { path: "/master/bmcmaster", exact: true, component: BMCPage },
  { path: "/master/bmcmaster/add-bmc", exact: true, component: AddBMCPage },
  {
    path: "/master/bmcmaster/view-bmc/:bmcId",
    exact: true,
    component: ViewBMCPage,
  },
  {
    path: "/master/bmcmaster/edit-bmc/:bmcId",
    exact: true,
    component: EditBMCPage,
  },
  { path: "/master/dcsmaster", exact: true, component: DCSPage },
  { path: "/master/dcsmaster/add-dcs", exact: true, component: AddDCSPage },
  {
    path: "/master/dcsmaster/view-dcs/:dcsId",
    exact: true,
    component: ViewDCSPage,
  },
  {
    path: "/master/dcsmaster/edit-dcs/:dcsId",
    exact: true,
    component: EditDCSPage,
  },
  { path: "/master/farmermaster", exact: true, component: FarmersPage },
  {
    path: "/master/farmermaster/add-farmers",
    exact: true,
    component: AddFarmersPage,
  },
  {
    path: "/master/farmermaster/edit-farmer/:editFarmerId",
    exact: true,
    component: EditFarmersPage,
  },
  {
    path: "/master/farmermaster/view-farmer/:editFarmerId",
    exact: true,
    component: ViewFarmersPage,
  },

  //iccc
  { path: "/icccmaster/vehiclemaster", exact: true, component: LiveTrackingPage },
  {
    path: "/icccmaster/vehiclemaster/add-vehicle",
    exact: true,
    component: AddVehiclesPage,
  },
  {
    path: "/icccmaster/vehiclemaster/view-vehicle/:vehicleId",
    exact: true,
    component: ViewVehiclesPage,
  },
  {
    path: "/icccmaster/vehiclemaster/edit-vehicle/:editVehicleId",
    exact: true,
    component: EditVehiclesPage,
  },
  { path: "/icccmaster/drivermaster", exact: true, component: DriversPage },
  {
    path: "/icccmaster/drivermaster/add-drivers",
    exact: true,
    component: AddDriversPage,
  },
  {
    path: "/icccmaster/drivermaster/view-driver/:driverId",
    exact: true,
    component: ViewDriversPage,
  },
  {
    path: "/icccmaster/drivermaster/edit-driver/:editDriverId",
    exact: true,
    component: EditDriversPage,
  },
  {
    path: "/icccmaster/gpsmaster",
    exact: true,
    component: GpsPage,
  },
  {
    path: "/icccmaster/gpsmaster/add-gps",
    exact: true,
    component: AddGpsPage,
  },
  {
    path: "/icccmaster/gpsmaster/view-gps/:gpsId",
    exact: true,
    component: ViewGpsPage,
  },
  {
    path: "/icccmaster/gpsmaster/edit-gps/:gpsId",
    exact: true,
    component: EditGpsPage,
  },
  {
    path: "/icccmaster/cctvmaster",
    exact: true,
    component: CctvPage,
  },
  {
    path: "/icccmaster/cctvmaster/add-cctv",
    exact: true,
    component: AddCctvPage,
  },
  {
    path: "/icccmaster/smartlocks",
    exact: true,
    component: SmartLockPage,
  },
  {
    path: "/icccmaster/smartlocks/add-smartlocks",
    exact: true,
    component: AddSmartLockPage,
  },
  {
    path: "/icccmaster/vehiclemapping",
    exact: true,
    component: MappingPage,
  },
  {
    path: "/icccmaster/vehiclemapping/add-mapping",
    exact: true,
    component: AddMappingPage,
  },

  // new
  {
    path: "/iccc/camera",
    exact: true,
    component: CameraPage,
  },
  {
    path: "/icccmaster/routemaster",
    exact: true,
    component: RouteCreationPage,
  },

  // New pages
  {
    path: "/icccmaster/routemaster/create-route",
    exact: true,
    component: CreateRoutePage,
  },

  {
    path: "/master/ratemaster",
    exact: true,
    component: RateMatrixPage,
  },

  {
    path: "/setting",
    exact: true,
    component: SettingPage,
  },
  {
    path: "/user-role-management",
    exact: true,
    component: UserRoleManagementPage,
  },
  {
    path: "/user-role-management/assign-role",
    exact: true,
    component: AssignRolePage,
  },

  {
    path: "/master/users/create",
    exact: true,
    component: CreateUserPage,
  },
  {
    path: "/master/users/view-users/:userId",
    exact: true,
    component: ViewUserPage,
  },
  {
    path: "/live-gps-tracking",
    exact: true,
    component: LiveGpsTrackingPage,
  },
  {
    path: "/trip-navigator",
    exact: true,
    component: TripNavigatorPage,
  },
  {
    path: "/trip-navigator/merge-tracking",
    exact: true,
    component: MergeTrackingPage,
  },
  {
    path: "/trip-navigator/merge-tracking/mapping-details",
    exact: true,
    component: MergeTrackingDetailedStatusPage,
  },
  {
    path: "/dcs/daily-report",
    exact: true,
    component: DcsDailyReportPage,
  },
  {
    path: "/reports/dcs-daily-report",
    exact: true,
    component: AdminDcsDailyReportPage,
  },
  {
    path: "/setting/dcs-secretary-details",
    exact: true,
    component: DcsSecretaryDetailsPage,
  },
  {
    path: "/maker/dashboard",
    exact: true,
    component: MakerDashboardPage,
  },
  // milk procurement and DBT
  {
    path: "/milk-procurement/dashboard",
    exact: true,
    component: ProcurementDashboardPage,
  },
  {
    path: "/milk-procurement/farmer-list",
    exact: true,
    component: ProcurementFarmerListPage,
  },
  {
    path: "/milk-procurement/farmer-list/view-dcs/:routeDcsId",
    exact: true,
    component: ProcurementFarmerListPage,
  },
  {
    path: "/milk-procurement/dcs-list",
    exact: true,
    component: ProcurementDcsListPage,
  },
  {
    path: "/milkprocurement/deductions",
    exact: true,
    component: ProcurementDeductionPage,
  },
  {
    path: "/milkprocurement/deductions/dcs-list",
    exact: true,
    component: DcsDeductionPage,
  },
  {
    path: "/milkprocurement/deductions/view/:routeDcsId",
    exact: true,
    component: ProcurementDeductionPage,
  },
  {
    path: "/milkprocurement/payout",
    exact: true,
    component: PayoutDcsListPage,
  },
  {
    path: "/milkprocurement/payout/farmer-list/:routeDcsId",
    exact: true,
    component: PayoutFarmerListPage,
  },
  {
    path: "/milkprocurement/paymentcycle",
    exact: true,
    component: AdminPaymentCyclePage,
  },
  {
    path: "/milkprocurement/finalpayment",
    exact: true,
    component: FinalPaymentSheetPage,
  },
  {
    path: "/milkprocurement/finalpayment/farmer-list/:routeDcsId",
    exact: true,
    component: FinalPaymentFarmerListPage,
  },
  {
    path: "/milkprocurement/paymentdetails",
    exact: true,
    component: PaymentListPage,
  },
  {
    path: "/milkprocurement/paymentdetails/uploadpayment",
    exact: true,
    component: UploadPaymentSheetPage,
  },

  {
    path: "/milkprocurement/reconciliation",
    exact: true,
    component: SamudraMilkReconcilationPage,
  },

  // checker
  {
    path: "/checker/dashboard",
    exact: true,
    component: CheckerDashboardPage,
  },
  {
    path: "/checker/farmer-list",
    exact: true,
    component: CheckerFarmerListPage,
  },
  {
    path: "/checker/farmer-list/view-farmer/:farmerId",
    exact: true,
    component: CheckerFarmerDetailsPage,
  },
  {
    path: "/checker/profile",
    exact: true,
    component: CheckerProfilePage,
  },
  {
    path: "/checker/self-profiling",
    exact: true,
    component: CheckerSelfProfilingPage,
  },

  // Approver
  {
    path: "/approver/dashboard",
    exact: true,
    component: ApproverDashboardPage,
  },
  {
    path: "/approver/farmer-list",
    exact: true,
    component: ApproverFarmerListPage,
  },
  {
    path: "/approver/farmer-list/view-farmer/:farmerId",
    exact: true,
    component: ApproverFarmerDetailsPage,
  },
  {
    path: "/approver/profile",
    exact: true,
    component: ApproverProfilePage,
  },
  {
    path: "/approver/self-profiling",
    exact: true,
    component: ApproverSelfProfilingPage,
  },

  // Admin
  //DBT-PROCUREMENT DASHBOARD
  {
    path: "/dashboard/milkprocurementdat",
    exact: true,
    component: ProcurementAdminDashboardPage,
  },
  {
    path: "/dashboard/cctv",
    exact: true,
    component: cctvDashboardPage,
  },
  {
    path: "/dashboard/qualitymonitoring",
    exact: true,
    component: QualityMonitoringPage,
  },
  //Quality Monitoring Dat Page
  {
    path: "/dashboard/qualitymonitoringdat",
    exact: true,
    component: QualityMonitoringDatPage,
  },
  //DCS Wise Procurement Report Page
  { path: "/dashboard/dcswiseprocreport", exact: true, component: DcsWiseProcurementReportPage },
  //DCS Wise Procurement Report Dat Page
  { path: "/dashboard/dcswiseprocreportdat", exact: true, component: DcsWiseProcurementReportDatPage },
  //Farmer Ledger Report Page
  { path: "/dashboard/farmerledgerreport", exact: true, component: FarmerLedgerReportPage },
  //vaishta sales dashboard Page
  { path: "/dashboard/unionsale", exact: true, component: VasishtaDashboardPage },
  //SAP purchase order dashboard Page
  { path: "/dashboard/sappurchaseorder", exact: true, component: SapDashboardPage },
  //SAP purchase order list (report) Page
  { path: "/dashboard/sappurchaseorder/report", exact: true, component: SapMaterialReportPage },
  //SAP material master Page
  { path: "/master/sapmaterial", exact: true, component: SapMaterialMasterPage },
  {
    path: "/reports/makerreport",
    exact: true,
    component: AdminDashboardPage,
  },
  {
    path: "/admin/dashboard/maker-details",
    exact: true,
    component: MakerDetailsPage,
  },
  {
    path: "/admin/dashboard/checker-details",
    exact: true,
    component: CheckerDetailsPage,
  },
  {
    path: "/admin/dashboard/approver-details",
    exact: true,
    component: ApproverDetailsPage,
  },
  {
    path: "/admin/data-dashboard",
    exact: true,
    component: AdminDataDashboardPage,
  },
  {
    path: "/admin/map-dashboard",
    exact: true,
    component: AdminMapDashboardPage,
  },
  {
    path: "/admin/heatmap-dashboard",
    exact: true,
    component: AdminHeatMapPage,
  },
  {
    path: "/admin/milk-analytics",
    exact: true,
    component: MilkAnalyticsPage,
  },
  {
    path: "/admin/product-analytics",
    exact: true,
    component: ProductAnalyticsPage,
  },
  {
    path: "/admin/retailer-map",
    exact: true,
    component: AdminRetailerPage,
  },
  {
    path: "/admin/dcs",
    exact: true,
    component: AdminDCSPage,
  },
  {
    path: "/admin/farmer",
    exact: true,
    component: AdminFarmerPage,
  },
  {
    path: "/admin/dcs/view-dcs/:dcsId",
    exact: true,
    component: ViewDcsPage,
  },
  {
    path: "/admin/farmer/view-farmer/:farmerId",
    exact: true,
    component: ViewFarmerPage,
  },
  {
    path: "/reports/dailyreport",
    exact: true,
    component: DailyReportPage,
  },
  {
    path: "/reports/unionwiwsereport",
    exact: true,
    component: UnionWiseReportPage,
  },
  // Union Admin
  {
    path: "/union/admin/dashboard",
    exact: true,
    component: UnionAdminDashboardPage,
  },
  {
    path: "/union/admin/farmer-list",
    exact: true,
    component: UnionAdminFarmerListPage,
  },
  {
    path: "/union/admin/dcs-list",
    exact: true,
    component: UnionAdminDcsListPage,
  },
  {
    path: "/union/admin/dashboard/procurement",
    exact: true,
    component: UnionProcurementDashboardPage,
  },

  {
    path: "/union/admin/view-dcs/:dcsId",
    exact: true,
    component: ViewDcsPage,
  },

  {
    path: "/union/admin/view-farmer/:farmerId",
    exact: true,
    component: ViewFarmerPage,
  },
  // user role manage
  {
    path: "/master/users",
    exact: true,
    component: AdminCreateUserPage,
  },
  {
    path: "/admin/create-group",
    exact: true,
    component: AdminCreateGroupPage,
  },
  {
    path: "/usermasters/usergroupmaster/edit-usergroup/:ugId",
    exact: true,
    component: AdminEditGroupPage,
  },
  {
    path: "/usermasters/usergroupmaster",
    exact: true,
    component: UserGroupTable,
  },
];

const PagesRoute = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const isPrivateRoute = privateRoutes.some((route) =>
    Boolean(
      matchPath({ path: route.path, end: route.exact }, location.pathname)
    )
  );

  useEffect(() => {
    // Check token from localStorage (via secureStorage)
    const hasToken = isTokenValid();

    if (isPrivateRoute && !hasToken) {
      navigate("/", { replace: true });
    }
  }, [isPrivateRoute, location.pathname, navigate]);

  return (
    <div>
      {!isPrivateRoute &&
        publicRoutes.map((route, idx) => (
          <AppRoute
            key={`public-${idx}`}
            path={route.path}
            exact={route.exact}
            component={route.component}
            isAuthProtected={false}
          />
        ))}

      {isPrivateRoute && (
        <Layout>
          <ProtectedRoute>
            {privateRoutes.map((route, idx) => (
              <AppRoute
                key={`private-${idx}`}
                path={route.path}
                exact={route.exact}
                component={route.component}
                isAuthProtected={true}
              />
            ))}
          </ProtectedRoute>
        </Layout>
      )}
    </div>
  );
};

export default PagesRoute;
